<div class="flex flex-col flex-auto min-w-0 h-full bg-gray-50 dark:bg-gray-900" *ngIf="!showManagement; else showUserManagement">

    <div class="sticky top-0 z-10 bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700">
        <div class="mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">

                <div>
                    <h1 class="text-2xl font-bold tracking-tight text-gray-900 dark:text-white truncate">
                        {{ title }}
                    </h1>
                    <p class="text-sm text-gray-500 dark:text-gray-400">View and manage system users and accounts</p>
                </div>

                <div class="flex flex-wrap items-center gap-3 self-start md:self-auto">
                    <button mat-stroked-button (click)="showFilters = !showFilters"
                            [color]="showFilters ? 'primary' : ''">
                        <mat-icon [svgIcon]="'heroicons_outline:adjustments-horizontal'"></mat-icon>
                        <span class="ml-2">Filters</span>
                    </button>

                    <button mat-flat-button color="primary" (click)="createNewUser()">
                        <mat-icon [svgIcon]="'heroicons_outline:plus'"></mat-icon>
                        <span class="ml-2">New User</span>
                    </button>
                </div>
            </div>

            <div class="mt-4 border-t pt-4 dark:border-gray-700" *ngIf="showFilters" @fadeIn>
                <form [formGroup]="searchForm" class="grid grid-cols-1 sm:grid-cols-12 gap-4">

                    <mat-form-field class="w-full fuse-mat-dense sm:col-span-6 lg:col-span-7" subscriptSizing="dynamic">
                        <mat-label>Search</mat-label>
                        <mat-icon matPrefix [svgIcon]="'heroicons_outline:magnifying-glass'"></mat-icon>
                        <input matInput formControlName="search" placeholder="Search by name, email, or keyword">
                    </mat-form-field>

                    <mat-form-field class="w-full fuse-mat-dense sm:col-span-4 lg:col-span-3" subscriptSizing="dynamic">
                        <mat-label>User Type</mat-label>
                        <mat-select formControlName="type">
                            <mat-option *ngFor="let item of accountTypes" [value]="item.value">
                                {{ item.view }}
                            </mat-option>
                        </mat-select>
                    </mat-form-field>

                    <div class="flex items-center sm:col-span-2 lg:col-span-2">
                        <mat-checkbox formControlName="inactive" color="primary">Include Inactive</mat-checkbox>
                    </div>

                </form>
            </div>
        </div>
    </div>

    <div class="flex-auto overflow-y-auto p-4 sm:p-6 lg:p-8">

        <ng-container *ngIf="tableElements?.length > 0; else noRecords">
            <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">

                <div *ngFor="let element of tableElements"
                     class="group relative flex flex-col bg-white dark:bg-card rounded-2xl shadow hover:shadow-lg transition-shadow duration-300 overflow-hidden border border-gray-100 dark:border-transparent">

                    <div class="h-1.5 w-full" [ngClass]="getUserTypeIndicatorColor(element.en_logintype)"></div>

                    <div class="p-5 flex flex-col flex-auto">
                        <div class="flex flex-wrap items-center justify-between mb-3 gap-2">
                            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
                                  [ngClass]="getUserTypeBadgeClass(element.en_logintype)">
                                {{ getUserTypeLabel(element.en_logintype) }}
                            </span>
                            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
                                  [ngClass]="getAccountStatusBadgeClass(element.en_accountstatus)">
                                {{ getAccountStatusLabel(element.en_accountstatus) }}
                            </span>
                        </div>

                        <h3 class="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1 leading-tight group-hover:text-primary transition-colors">
                            {{ getFullName(element) }}
                        </h3>
                        <p class="text-xs text-gray-400 font-mono mb-4">ID: {{ element.id_user }}</p>

                        <div class="text-sm text-gray-600 dark:text-gray-400 mb-6 flex-auto break-all">
                            <mat-icon class="icon-size-4 align-middle mr-1" [svgIcon]="'heroicons_outline:envelope'"></mat-icon>
                            {{ element.ds_email || 'No email provided' }}
                        </div>

                        <div class="pt-4 border-t border-gray-100 dark:border-gray-700 flex flex-col gap-2 text-xs text-gray-500 dark:text-gray-400 mb-4">
                            <div class="flex items-center justify-between">
                                <span>Login Attempts:</span>
                                <span class="font-medium text-gray-900 dark:text-gray-200">{{ element.vi_loginattempts }}</span>
                            </div>
                            <div class="flex items-center justify-between" *ngIf="element.dt_created?.datetime?.date">
                                <span>Created:</span>
                                <span class="font-medium text-gray-900 dark:text-gray-200">{{ element.dt_created.datetime.date | date:'mediumDate' }}</span>
                            </div>
                        </div>

                        <div class="mt-auto pt-2 flex items-center justify-between">
                            <button mat-button color="primary" (click)="switchToUser(element.id_user)">
                                <span class="flex items-center">
                                    Manage
                                    <mat-icon iconPositionEnd [svgIcon]="'heroicons_outline:arrow-long-right'" class="ml-1 icon-size-4"></mat-icon>
                                </span>
                            </button>
                        </div>

                    </div>
                </div>

            </div>
        </ng-container>

        <ng-template #noRecords>
            <div class="flex flex-col items-center justify-center p-12 text-center opacity-75">
                <div class="bg-gray-100 p-4 rounded-full dark:bg-gray-800 mb-4">
                    <mat-icon class="icon-size-12 text-gray-400"
                              [svgIcon]="'heroicons_outline:magnifying-glass'"></mat-icon>
                </div>
                <div class="text-xl font-semibold text-gray-800 dark:text-gray-200">{{ loadingMessage }}</div>
                <p class="text-gray-500 mt-2">Try adjusting your filters or search terms.</p>
                <ngx-spinner bdColor="rgba(0,0,0,0)" color="#6366f1" size="medium" type="ball-clip-rotate"></ngx-spinner>
            </div>
        </ng-template>

    </div>

    <div class="bg-white dark:bg-card border-t dark:border-gray-700 p-2">
        <mat-paginator
            [disabled]="showManagement"
            (page)="searchElements('paginator')"
            [length]="20"
            [pageSize]="20"
            [pageSizeOptions]="[10, 20, 50, 100]"
            [showFirstLastButtons]="true"
            class="bg-transparent">
        </mat-paginator>
    </div>
</div>

<ng-template #showUserManagement>
    <user-management
        class="w-full h-full block overflow-hidden"
        [userId]="selectedUserId"
        (commandEvent)="handleCommand($event)">
    </user-management>
</ng-template>
